import { io } from 'socket.io-client';
import store from '../store';

let socket = null;
let latestScopes = { lists: [], groups: [] };
let healthCheckTimer = null;
let healthCheckInFlight = false;
let lastServerActivityAt = 0;

const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const socketUrl = process.env.VUE_APP_WS_URL || (isLocal ? 'http://localhost:3099' : 'wss://notas.websockets.gestiontotal.app');
const HEARTBEAT_INTERVAL = 8000;
const HEARTBEAT_TIMEOUT = 3500;
const STALE_AFTER_MS = 16000;

function currentSocketToken() {
  return localStorage.getItem('notas_access_token') || '';
}

function currentDeviceId() {
  return localStorage.getItem('notas_device_id') || '';
}

function normalizeScopes({ lists = [], groups = [] } = {}) {
  return {
    lists: [...new Set((lists || []).filter(Boolean))],
    groups: [...new Set((groups || []).filter(Boolean))],
  };
}

function touchServerActivity() {
  lastServerActivityAt = Date.now();
}

function syncSocketAuth() {
  if (!socket) return;
  socket.auth = { token: currentSocketToken() };
}

function clearHealthCheck() {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
    healthCheckTimer = null;
  }
  healthCheckInFlight = false;
}

function emitWithAck(event, payload = {}, timeout = HEARTBEAT_TIMEOUT) {
  if (!socket) {
    return Promise.resolve({ success: false, message: 'Socket unavailable' });
  }

  return new Promise((resolve) => {
    try {
      socket.timeout(timeout).emit(event, payload, (error, response) => {
        if (error) {
          resolve({ success: false, timeout: true, message: error?.message || 'Ack timeout' });
          return;
        }
        resolve(response || { success: true });
      });
    } catch (error) {
      resolve({ success: false, message: error?.message || 'Emit failed' });
    }
  });
}

async function pingSocket() {
  if (!socket?.connected) return false;
  const response = await emitWithAck('notes:ping', {
    at: Date.now(),
    lists: latestScopes.lists,
    groups: latestScopes.groups,
  });
  if (response?.success === false) {
    return false;
  }
  touchServerActivity();
  return true;
}

async function emitLatestScopes() {
  if (!socket?.connected) return false;
  const scopes = normalizeScopes(latestScopes);
  const response = await emitWithAck('notes:subscribe', scopes, 5000);
  if (response?.success === false) {
    console.warn('notes:subscribe rejected', response?.message || response);
    return false;
  }
  touchServerActivity();
  return true;
}

function buildSocket() {
  const token = currentSocketToken();
  if (!token) return null;

  const instance = io(socketUrl, {
    transports: ['polling', 'websocket'],
    auth: { token },
    timeout: 15000,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 600,
    reconnectionDelayMax: 4000,
    randomizationFactor: 0.35,
    withCredentials: true,
    autoConnect: true,
  });

  instance.on('connect', async () => {
    socket = instance;
    touchServerActivity();
    store.commit('setSocketConnected', true);
    startHealthCheck();
    await emitLatestScopes();
    try {
      await store.dispatch('pullRemote');
    } catch (error) {
      console.warn(error);
    }
  });

  instance.on('disconnect', () => {
    if (socket === instance) {
      store.commit('setSocketConnected', false);
    }
  });

  instance.on('connect_error', (error) => {
    if (socket === instance) {
      store.commit('setSocketConnected', false);
    }
    console.warn('Socket connect_error', error?.message || error);
  });

  instance.on('notes:relay', async (payload = {}) => {
    try {
      touchServerActivity();
      const operation = payload?.operation || null;
      if (!operation) return;
      if (String(operation.device_id || '') === String(currentDeviceId())) return;
      await store.dispatch('applyRealtimeRelay', operation);
    } catch (error) {
      console.warn('notes:relay error', error);
    }
  });

  instance.on('notes:changed', async (payload = {}) => {
    touchServerActivity();
    await store.dispatch('confirmPendingFromServerEvent', payload || {});
    await store.dispatch('pullRemote');
    await store.dispatch('broadcastLocalRefresh');
  });

  instance.on('group:member_joined', async () => {
    touchServerActivity();
    await store.dispatch('bootstrap');
    await store.dispatch('broadcastLocalRefresh');
  });

  instance.on('notifications:changed', (payload = {}) => {
    touchServerActivity();
    window.dispatchEvent(new CustomEvent('notas:notifications-changed', { detail: payload || {} }));
  });

  instance.on('notifications:read', (payload = {}) => {
    touchServerActivity();
    window.dispatchEvent(new CustomEvent('notas:notifications-read', { detail: payload || {} }));
  });

  return instance;
}

function destroySocket({ preserveScopes = true } = {}) {
  clearHealthCheck();
  if (socket) {
    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch (error) {
      console.warn(error);
    }
  }
  socket = null;
  store.commit('setSocketConnected', false);
  if (!preserveScopes) {
    latestScopes = { lists: [], groups: [] };
  }
}

function startHealthCheck() {
  clearHealthCheck();
  healthCheckTimer = window.setInterval(async () => {
    if (healthCheckInFlight) return;
    if (document.hidden || !navigator.onLine || !currentSocketToken()) return;
    healthCheckInFlight = true;
    try {
      const stale = lastServerActivityAt > 0 && (Date.now() - lastServerActivityAt) > STALE_AFTER_MS;
      if (!socket?.connected || stale) {
        await hardReconnectSocket(stale ? 'stale-socket' : 'disconnected-interval');
        return;
      }
      const ok = await pingSocket();
      if (!ok) {
        await hardReconnectSocket('heartbeat-timeout');
      }
    } finally {
      healthCheckInFlight = false;
    }
  }, HEARTBEAT_INTERVAL);
}

if (typeof window !== 'undefined' && !window.__notasSocketSessionEventsBound) {
  window.__notasSocketSessionEventsBound = true;

  window.addEventListener('notas:token-refreshed', async () => {
    if (!currentSocketToken()) return;
    await ensureSocketHealthy({ forceReconnect: !socket?.connected, reason: 'token-refreshed' });
  });

  window.addEventListener('notas:auth-invalid', () => {
    disconnectSocket();
  });

  window.addEventListener('pagehide', () => {
    disconnectSocket({ preserveScopes: true, silent: true });
  });

  window.addEventListener('pageshow', async (event) => {
    if (!currentSocketToken()) return;
    await ensureSocketHealthy({ forceReconnect: !!event.persisted, reason: event.persisted ? 'pageshow-bfcache' : 'pageshow' });
  });
}

export function connectSocket() {
  const token = currentSocketToken();
  if (!token) return null;

  if (socket) {
    syncSocketAuth();
    if (!socket.connected) {
      socket.connect();
    } else {
      emitLatestScopes().catch((error) => console.warn(error));
    }
    startHealthCheck();
    return socket;
  }

  socket = buildSocket();
  return socket;
}

export async function ensureSocketHealthy({ forceReconnect = false, reason = 'manual' } = {}) {
  const token = currentSocketToken();
  if (!token) {
    disconnectSocket();
    return null;
  }

  if (forceReconnect) {
    return hardReconnectSocket(reason);
  }

  if (!socket) {
    return connectSocket();
  }

  syncSocketAuth();

  if (!socket.connected) {
    socket.connect();
    return socket;
  }

  const ok = await pingSocket();
  if (!ok) {
    return hardReconnectSocket(`${reason}:ping-failed`);
  }

  await emitLatestScopes();
  return socket;
}

export async function hardReconnectSocket(reason = 'manual') {
  const token = currentSocketToken();
  if (!token) {
    disconnectSocket();
    return null;
  }

  destroySocket({ preserveScopes: true });
  socket = buildSocket();
  if (!socket) {
    console.warn('Socket hard reconnect skipped', reason);
  }
  return socket;
}

export async function emitOptimisticOperation(operation) {
  if (!operation) return false;

  if (!socket?.connected) {
    await ensureSocketHealthy({ reason: 'relay-preflight' });
  }

  if (!socket?.connected) return false;

  const response = await emitWithAck('notes:relay', {
    operation: {
      ...operation,
      device_id: operation.device_id || currentDeviceId(),
    },
  }, 2200);

  if (response?.success === false) {
    console.warn('notes:relay rejected', response?.message || response);
    return false;
  }

  touchServerActivity();
  return true;
}

export function subscribeScopes({ lists = [], groups = [] } = {}) {
  latestScopes = normalizeScopes({ lists, groups });
  if (socket?.connected) {
    emitLatestScopes().catch((error) => console.warn(error));
  }
  return latestScopes;
}

export function disconnectSocket({ preserveScopes = false, silent = false } = {}) {
  destroySocket({ preserveScopes });
  if (!silent && !preserveScopes) {
    latestScopes = { lists: [], groups: [] };
  }
}
